import {ChangeEvent, FC, ReactNode, useMemo} from 'react';
//formik
import {FieldInputProps, FormikProps} from 'formik';
//mui
import {RadioProps, Checkbox as MuiCheckbox, FormControlLabel, Box, SxProps, Theme} from '@mui/material';
//icons
import {ReactComponent as CheckedPrimaryIcon} from '@icons/checkbox-checked.svg';
import {ReactComponent as CheckedErrorIcon} from '@icons/close-square.svg';
import {ReactComponent as EmptyStarIcon} from '@icons/star-empty.svg';
import {ReactComponent as CheckedStarIcon} from '@icons/star-filled.svg';
import {ReactComponent as UncheckedIcon} from '@icons/checkbox-unchecked.svg';
//constants
import {PRIMARY} from '@/utils/constants';
//components
import FormHelperText from '../FormHelperText';
//styles
import {checkboxStyle} from './Checkbox.styles';

export interface ExtendedCheckboxProps extends Omit<RadioProps, 'form'> {
	value?: boolean;
	label?: string | ReactNode;
	field?: FieldInputProps<string | number>;
	form?: FormikProps<any>;
	isStarIcon?: boolean;
	disableErrorMessage?: boolean;
	sx?: SxProps<Theme>;
}

const Checkbox: FC<ExtendedCheckboxProps> = ({
	color = PRIMARY,
	disabled = false,
	value = false,
	form,
	field,
	isStarIcon,
	onChange,
	label,
	disableErrorMessage,
	sx,
	...rest
}) => {
	const isChecked = field ? !!field.value : !!value;
	const changeHandler = (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
		if (onChange) {
			onChange(event, checked);
		} else if (form && field) {
			form.setFieldValue(field.name, checked);
		}
	};

	const errorText = useMemo(() => {
		return (field?.name && form?.touched[field?.name] && (form?.errors[field?.name] as string)) || undefined;
	}, [form?.errors, form?.touched, field]);

	const checkedIcon = useMemo(() => {
		if (isStarIcon) return color === PRIMARY ? <CheckedStarIcon /> : <EmptyStarIcon />;
		return color === PRIMARY ? <CheckedPrimaryIcon /> : <CheckedErrorIcon />;
	}, [isStarIcon, color, isChecked]);

	const uncheckedIcon = useMemo(() => (isStarIcon ? <EmptyStarIcon /> : <UncheckedIcon />), []);

	return (
		<Box sx={checkboxStyle.wrapper}>
			<FormControlLabel
				sx={{...sx, ...checkboxStyle.label}}
				value={value}
				label={label}
				control={
					<MuiCheckbox
						{...rest}
						checked={isChecked}
						checkedIcon={checkedIcon}
						icon={uncheckedIcon}
						onChange={changeHandler}
						value={value}
						color={color}
						disabled={disabled}
					/>
				}
			/>

			{!disableErrorMessage && errorText && <FormHelperText error title={errorText} sx={checkboxStyle.error} />}
		</Box>
	);
};

export default Checkbox;
